import React, { useState, useEffect } from 'react'
import { postsAPI, categoriesAPI, mediaAPI, usersAPI } from '../services/api'
import { Post, Category, User, PostFormData } from '../types'
import './Posts.css'

const Posts: React.FC = () => {
  const [posts, setPosts] = useState<Post[]>([])
  const [categories, setCategories] = useState<Category[]>([])
  const [users, setUsers] = useState<User[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [categoryFilter, setCategoryFilter] = useState('')
  const [showPostForm, setShowPostForm] = useState(false)
  const [editingPost, setEditingPost] = useState<Post | null>(null)
  const [formData, setFormData] = useState<PostFormData>({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    author_id: '',
    category_id: '',
    status: 'draft',
    is_featured: false,
    meta_title: '',
    meta_description: '',
    tags: [],
    media_type: 'text',
    media_url: '',
    media_duration: ''
  })
  const [isUploading, setIsUploading] = useState(false)
  const [isUploadingFeaturedImage, setIsUploadingFeaturedImage] = useState(false)
  const [newTag, setNewTag] = useState('')

  useEffect(() => {
    loadData()
  }, [currentPage, searchTerm, statusFilter, categoryFilter]) // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-generate slug from title
  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remove diacritics
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-+|-+$/g, '') // Remove leading and trailing dashes
  }

  // Update slug when title changes (only for new posts)
  useEffect(() => {
    if (!editingPost && formData.title) {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(formData.title)
      }))
    }
  }, [formData.title, editingPost])

  // Auto-generate meta title from title if empty
  useEffect(() => {
    if (formData.title && !formData.meta_title) {
      setFormData(prev => ({
        ...prev,
        meta_title: formData.title
      }))
    }
  }, [formData.title, formData.meta_title])

  // Convert duration from seconds to HH:MM:SS format
  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600)
    const minutes = Math.floor((seconds % 3600) / 60)
    const secs = seconds % 60
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`
  }

  // Convert duration from HH:MM:SS format to seconds
  const parseDuration = (duration: string): number => {
    const parts = duration.split(':').map(p => parseInt(p) || 0)
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2]
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1]
    } else if (parts.length === 1) {
      return parts[0]
    }
    return 0
  }

  // Tags management
  const addTag = () => {
    if (newTag.trim() && !formData.tags.includes(newTag.trim())) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, newTag.trim()]
      }))
      setNewTag('')
    }
  }

  const removeTag = (tagToRemove: string) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter(tag => tag !== tagToRemove)
    }))
  }

  const handleFeaturedImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setIsUploadingFeaturedImage(true)
      const result = await mediaAPI.upload(file)
      setFormData({
        ...formData,
        featured_image: result.media.url
      })
    } catch (error) {
      console.error('Failed to upload featured image:', error)
      alert('Không thể tải lên ảnh đại diện. Vui lòng thử lại.')
    } finally {
      setIsUploadingFeaturedImage(false)
    }
  }

  const loadData = async () => {
    try {
      setIsLoading(true)
      const [postsData, categoriesData, usersData] = await Promise.all([
        postsAPI.getAll({
          page: currentPage,
          limit: 10,
          search: searchTerm || undefined,
          status: statusFilter || undefined,
          category: categoryFilter || undefined
        }),
        categoriesAPI.getAll(),
        usersAPI.getAll()
      ])

      setPosts(postsData.posts || [])
      setTotalPages(postsData.pagination?.totalPages || 1)
      setCategories(categoriesData.categories || [])
      setUsers(usersData.users || [])
    } catch (error) {
      console.error('Failed to load posts:', error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa bài viết này?')) {
      return
    }

    try {
      await postsAPI.delete(id)
      loadData() // Reload data
    } catch (error) {
      console.error('Failed to delete post:', error)
      alert('Không thể xóa bài viết')
    }
  }

  const handleEdit = (post: Post) => {
    setEditingPost(post)
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      featured_image: post.featured_image || '',
      author_id: post.author_id.toString(),
      category_id: post.category_id.toString(),
      status: post.status,
      is_featured: post.is_featured,
      meta_title: post.meta_title || '',
      meta_description: post.meta_description || '',
      tags: post.tags || [],
      media_type: post.media_type || 'text',
      media_url: post.media_url || '',
      media_duration: post.media_duration ? formatDuration(post.media_duration) : ''
    })
    setShowPostForm(true)
  }

  const handleCreate = () => {
    setEditingPost(null)
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featured_image: '',
      author_id: users.length > 0 ? users[0].id.toString() : '',
      category_id: '',
      status: 'draft',
      is_featured: false,
      meta_title: '',
      meta_description: '',
      tags: [],
      media_type: 'text',
      media_url: '',
      media_duration: ''
    })
    setShowPostForm(true)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    // Validation based on media type
    if (!formData.title.trim()) {
      alert('Vui lòng điền tiêu đề')
      return
    }

    if (!formData.slug.trim()) {
      alert('Vui lòng điền slug')
      return
    }

    if (formData.media_type === 'text') {
      if (!formData.content.trim() || !formData.category_id) {
        alert('Vui lòng điền nội dung và chọn danh mục cho bài viết văn bản')
        return
      }
    }

    if ((formData.media_type === 'video' || formData.media_type === 'podcast') && !formData.media_url) {
      alert(`Vui lòng tải lên file ${formData.media_type === 'video' ? 'video' : 'audio'}`)
      return
    }

    try {
      const submitData = {
        title: formData.title.trim(),
        slug: formData.slug.trim(),
        content: formData.content.trim(),
        excerpt: formData.excerpt.trim() || undefined,
        featured_image: formData.featured_image || undefined,
        author_id: parseInt(formData.author_id),
        category_id: parseInt(formData.category_id) || undefined,
        status: formData.status,
        is_featured: formData.is_featured,
        meta_title: formData.meta_title.trim() || undefined,
        meta_description: formData.meta_description.trim() || undefined,
        tags: formData.tags,
        media_type: formData.media_type,
        media_url: formData.media_url || undefined,
        media_duration: formData.media_duration ? parseDuration(formData.media_duration) : undefined
      }
      
      if (editingPost) {
        await postsAPI.update(editingPost.id.toString(), submitData)
        alert('Cập nhật bài viết thành công!')
      } else {
        await postsAPI.create(submitData)
        alert('Tạo bài viết thành công!')
      }
      
      setShowPostForm(false)
      setEditingPost(null)
      loadData()
    } catch (error) {
      console.error('Failed to save post:', error)
      alert('Không thể lưu bài viết')
    }
  }

  const handleCancel = () => {
    setShowPostForm(false)
    setEditingPost(null)
  }

  const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    try {
      setIsUploading(true)
      const result = await mediaAPI.upload(file)
      setFormData({
        ...formData,
        media_url: result.media.url
      })
    } catch (error) {
      console.error('Failed to upload file:', error)
      alert('Không thể tải lên file. Vui lòng thử lại.')
    } finally {
      setIsUploading(false)
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    })
  }

  if (isLoading) {
    return <div className="loading">Đang tải...</div>
  }

  return (
    <div className="posts-page">
      <div className="page-header">
        <h2>Quản lý bài viết</h2>
        <button className="btn btn-primary" onClick={handleCreate}>+ Thêm bài viết mới</button>
      </div>

      <div className="posts-filters">
        <input
          type="text"
          placeholder="Tìm kiếm bài viết..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
        
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">Tất cả trạng thái</option>
          <option value="published">Đã xuất bản</option>
          <option value="draft">Bản nháp</option>
          <option value="archived">Lưu trữ</option>
        </select>

        <select
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
          className="filter-select"
        >
          <option value="">Tất cả danh mục</option>
          {categories.map(category => (
            <option key={category.id} value={category.slug}>
              {category.name}
            </option>
          ))}
        </select>
      </div>

      <div className="posts-table">
        <table>
          <thead>
            <tr>
              <th>Tiêu đề</th>
              <th>Tác giả</th>
              <th>Danh mục</th>
              <th>Loại</th>
              <th>Trạng thái</th>
              <th>Ngày tạo</th>
              <th>Lượt xem</th>
              <th>Thao tác</th>
            </tr>
          </thead>
          <tbody>
            {posts.length > 0 ? posts.map(post => (
              <tr key={post.id}>
                <td>
                  <div className="post-title">
                    <strong>{post.title}</strong>
                    {post.is_featured && <span className="featured-badge">Nổi bật</span>}
                    {post.excerpt && <p className="post-excerpt">{post.excerpt}</p>}
                  </div>
                </td>
                <td>{post.author_name}</td>
                <td>{post.category_name}</td>
                <td>
                  <span className={`media-type-badge ${post.media_type || 'text'}`}>
                    {post.media_type === 'video' ? '📹 Video' : 
                     post.media_type === 'podcast' ? '🎧 Podcast' : '📄 Văn bản'}
                  </span>
                </td>
                <td>
                  <span className={`status-badge ${post.status}`}>
                    {post.status === 'published' ? 'Đã xuất bản' : 
                     post.status === 'draft' ? 'Bản nháp' : 'Lưu trữ'}
                  </span>
                </td>
                <td>{formatDate(post.created_at)}</td>
                <td>{post.view_count}</td>
                <td>
                  <div className="action-buttons">
                    <button 
                      className="btn btn-sm btn-edit" 
                      onClick={() => handleEdit(post)}
                    >
                      Sửa
                    </button>
                    <button 
                      className="btn btn-sm btn-delete" 
                      onClick={() => handleDelete(post.id.toString())}
                    >
                      Xóa
                    </button>
                  </div>
                </td>
              </tr>
            )) : (
              <tr>
                <td colSpan={8} className="no-data">
                  Không có bài viết nào
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="pagination">
          <button 
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="btn btn-sm"
          >
            « Trước
          </button>
          
          <span className="page-info">
            Trang {currentPage} / {totalPages}
          </span>
          
          <button 
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="btn btn-sm"
          >
            Sau »
          </button>
        </div>
      )}

      {showPostForm && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingPost ? 'Sửa bài viết' : 'Thêm bài viết mới'}</h3>
              <button className="modal-close" onClick={handleCancel}>×</button>
            </div>
            
            <form onSubmit={handleSubmit} className="post-form">
              {/* Basic Information Section */}
              <div className="form-section">
                <h4 className="form-section-title">Thông tin cơ bản</h4>
                
                <div className="form-group">
                  <label htmlFor="media_type">Loại media *</label>
                  <select
                    id="media_type"
                    value={formData.media_type}
                    onChange={(e) => setFormData({...formData, media_type: e.target.value as any})}
                  >
                    <option value="text">📄 Văn bản</option>
                    <option value="video">📹 Video</option>
                    <option value="podcast">🎧 Podcast</option>
                  </select>
                </div>

                <div className="form-group">
                  <label htmlFor="title">Tiêu đề *</label>
                  <input
                    id="title"
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({...formData, title: e.target.value})}
                    placeholder="Nhập tiêu đề bài viết"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="slug">Slug *</label>
                  <input
                    id="slug"
                    type="text"
                    value={formData.slug}
                    onChange={(e) => setFormData({...formData, slug: e.target.value})}
                    placeholder="duong-dan-url-cua-bai-viet"
                    required
                  />
                  <small className="form-hint">Đường dẫn URL của bài viết (tự động tạo từ tiêu đề)</small>
                </div>

                <div className="form-group">
                  <label htmlFor="excerpt">Tóm tắt</label>
                  <textarea
                    id="excerpt"
                    value={formData.excerpt}
                    onChange={(e) => setFormData({...formData, excerpt: e.target.value})}
                    placeholder="Nhập tóm tắt bài viết"
                    rows={3}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="author">Tác giả *</label>
                    <select
                      id="author"
                      value={formData.author_id}
                      onChange={(e) => setFormData({...formData, author_id: e.target.value})}
                      required
                    >
                      {users.map(user => (
                        <option key={user.id} value={user.id}>
                          {user.username} ({user.email})
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="category">
                      Danh mục {formData.media_type === 'text' ? '*' : '(tùy chọn)'}
                    </label>
                    <select
                      id="category"
                      value={formData.category_id}
                      onChange={(e) => setFormData({...formData, category_id: e.target.value})}
                      required={formData.media_type === 'text'}
                    >
                      <option value="">Chọn danh mục</option>
                      {categories.map(category => (
                        <option key={category.id} value={category.id}>
                          {category.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="status">Trạng thái</label>
                    <select
                      id="status"
                      value={formData.status}
                      onChange={(e) => setFormData({...formData, status: e.target.value as any})}
                    >
                      <option value="draft">Bản nháp</option>
                      <option value="published">Đã xuất bản</option>
                      <option value="archived">Lưu trữ</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={formData.is_featured}
                        onChange={(e) => setFormData({...formData, is_featured: e.target.checked})}
                      />
                      Bài viết nổi bật
                    </label>
                  </div>
                </div>
              </div>

              {/* Content Section */}
              {formData.media_type === 'text' && (
                <div className="form-section">
                  <h4 className="form-section-title">Nội dung</h4>
                  <div className="form-group">
                    <label htmlFor="content">Nội dung *</label>
                    <textarea
                      id="content"
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      placeholder="Nhập nội dung bài viết"
                      rows={12}
                      required
                    />
                  </div>
                </div>
              )}

              {/* Media Section */}
              {(formData.media_type === 'video' || formData.media_type === 'podcast') && (
                <div className="form-section">
                  <h4 className="form-section-title">Media</h4>
                  
                  <div className="form-group">
                    <label htmlFor="media_file">
                      Tải lên file {formData.media_type === 'video' ? 'Video' : 'Audio'} *
                    </label>
                    <input
                      id="media_file"
                      type="file"
                      accept={formData.media_type === 'video' ? 'video/mp4,video/webm,video/mov' : 'audio/mp3,audio/wav,audio/ogg,audio/m4a'}
                      onChange={handleFileUpload}
                      className="file-input"
                      disabled={isUploading}
                    />
                    {isUploading && (
                      <div className="upload-progress">
                        <span>⏳ Đang tải lên...</span>
                      </div>
                    )}
                    {formData.media_url && !isUploading && (
                      <div className="uploaded-file-info">
                        <span>✅ File đã tải lên thành công</span>
                      </div>
                    )}
                    <div className="file-info">
                      <small>
                        Định dạng hỗ trợ: {formData.media_type === 'video' ? 'MP4, WebM, MOV (tối đa 100MB)' : 'MP3, WAV, OGG, M4A (tối đa 50MB)'}
                      </small>
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="media_duration">Thời lượng</label>
                    <input
                      id="media_duration"
                      type="text"
                      value={formData.media_duration}
                      onChange={(e) => setFormData({...formData, media_duration: e.target.value})}
                      placeholder="00:05:30 (giờ:phút:giây)"
                      pattern="[0-9]{2}:[0-9]{2}:[0-9]{2}"
                    />
                    <small className="form-hint">Định dạng: HH:MM:SS (ví dụ: 00:05:30)</small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="featured_image_file">Ảnh đại diện</label>
                    <input
                      id="featured_image_file"
                      type="file"
                      accept="image/jpeg,image/jpg,image/png,image/webp"
                      onChange={handleFeaturedImageUpload}
                      className="file-input"
                      disabled={isUploadingFeaturedImage}
                    />
                    {isUploadingFeaturedImage && (
                      <div className="upload-progress">
                        <span>⏳ Đang tải lên ảnh...</span>
                      </div>
                    )}
                    {formData.featured_image && !isUploadingFeaturedImage && (
                      <div className="featured-image-preview">
                        <img src={formData.featured_image} alt="Featured" style={{maxWidth: '200px', maxHeight: '120px', objectFit: 'cover'}} />
                        <button type="button" onClick={() => setFormData({...formData, featured_image: ''})} className="btn btn-sm btn-danger">
                          Xóa ảnh
                        </button>
                      </div>
                    )}
                    <small className="form-hint">Ảnh đại diện cho video/podcast (JPEG, PNG, WebP - tối đa 5MB)</small>
                  </div>
                </div>
              )}

              {/* Tags Section */}
              <div className="form-section">
                <h4 className="form-section-title">Tags</h4>
                <div className="form-group">
                  <label htmlFor="tags">Tags</label>
                  <div className="tags-input-container">
                    <input
                      id="tags"
                      type="text"
                      value={newTag}
                      onChange={(e) => setNewTag(e.target.value)}
                      placeholder="Nhập tag và nhấn Enter"
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault()
                          addTag()
                        }
                      }}
                    />
                    <button type="button" onClick={addTag} className="btn btn-sm btn-secondary">
                      Thêm
                    </button>
                  </div>
                  {formData.tags.length > 0 && (
                    <div className="tags-list">
                      {formData.tags.map((tag, index) => (
                        <span key={index} className="tag-chip">
                          {tag}
                          <button type="button" onClick={() => removeTag(tag)} className="tag-remove">×</button>
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* SEO Section */}
              <div className="form-section">
                <h4 className="form-section-title">SEO</h4>
                <div className="form-group">
                  <label htmlFor="meta_title">Meta Title</label>
                  <input
                    id="meta_title"
                    type="text"
                    value={formData.meta_title}
                    onChange={(e) => setFormData({...formData, meta_title: e.target.value})}
                    placeholder="Tiêu đề SEO (tự động lấy từ tiêu đề chính)"
                    maxLength={60}
                  />
                  <small className="form-hint">Độ dài khuyến nghị: 50-60 ký tự</small>
                </div>

                <div className="form-group">
                  <label htmlFor="meta_description">Meta Description</label>
                  <textarea
                    id="meta_description"
                    value={formData.meta_description}
                    onChange={(e) => setFormData({...formData, meta_description: e.target.value})}
                    placeholder="Mô tả ngắn gọn cho SEO"
                    rows={3}
                    maxLength={160}
                  />
                  <small className="form-hint">Độ dài khuyến nghị: 120-160 ký tự</small>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={handleCancel}>
                  Hủy
                </button>
                <button type="submit" className="btn btn-primary">
                  {editingPost ? 'Cập nhật' : 'Tạo mới'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default Posts